/**
 * Cambio de idioma único para toda la app (web pública y panel).
 *
 * El idioma NO puede vivir solo en memoria: cada página fuerza el idioma que
 * codifica su URL (ver `useLocalizedRoute`), así que un `setLang` sin navegar
 * se revierte al instante. Este hook hace las dos cosas: fija el idioma y
 * navega a la URL equivalente (pública o privada), preservando query y hash.
 */
import { useCallback } from "react";
import { useNavigate } from "@/lib/router-compat";
import { useLanguage } from "@/i18n/LanguageContext";
import { getLocalizedPath } from "@/i18n/routes";
import type { Lang } from "@/i18n/translations";

export const useLanguageSwitch = () => {
  const { lang, setLang, localizedPaths } = useLanguage();
  const navigate = useNavigate();

  const switchLang = useCallback(
    (next: Lang) => {
      setLang(next);
      if (typeof window === "undefined") return;

      const override = localizedPaths
        ? next === "es"
          ? localizedPaths.pathEs
          : localizedPaths.pathEn
        : null;
      const targetPath = override || getLocalizedPath(window.location.pathname, next);
      if (!targetPath || targetPath === window.location.pathname) return;

      const { search, hash } = window.location;
      navigate(`${targetPath}${search}${hash}`, { replace: true });
    },
    [localizedPaths, navigate, setLang],
  );

  const toggleLang = useCallback(() => {
    switchLang(lang === "es" ? "en" : "es");
  }, [lang, switchLang]);

  return { lang, switchLang, toggleLang };
};
